<template>
  <div>
    <div v-if="!isPremium">
      <UpgradeToPremium />
    </div>
    <div v-else-if="!portfolioError && portfolios.length > 0">

      <ecommerce-statistics
        class="saved-portfolios-card-with-hint"
        title="Saved Portfolios"
        explanation="A summary of saved portfolios with tools and table listing"
        :updated="charts.portfolioSummaryUpdated"
        :data="charts.portfolioSummary"
        :loading="loading"
      >
        <template #hint>
          <hint-popup
            :id="9"
            class="saved-portfolios-hint-popup"
          />
        </template>
      </ecommerce-statistics>

      <b-row>
        <b-col xl="3">
          <b-row class="match-height">
            <b-col
              xl="12"
              md="12"
            >
              <future-maturities-chart
                :future-maturities-data="{ futurematurities: charts.futureMaturities }"
                :compact="true"
                :loading="loading"
              />
            </b-col>
            <b-col
              xl="12"
              md="4"
            >
              <ecommerce-pie-chart
                :loading="loading"
                :data="charts.underlyings"
              />
            </b-col>
            <b-col
              xl="12"
              md="4"
            >
              <ecommerce-pie-chart
                :loading="loading"
                :data="charts.issuers"
              />
            </b-col>
            <b-col
              xl="12"
              md="4"
            >
              <ecommerce-pie-chart
                :loading="loading"
                :data="charts.productTypes"
              />
            </b-col>
          </b-row>
        </b-col>

        <b-col xl="9">
          <!-- Searchbar
          <div class="mb-2 d-flex justify-content-between">
            <b-input-group class="input-group-merge w-25">
              <b-form-input
                placeholder="Search portfolio"
                class="search-product"
              />
              <b-input-group-append is-text>
                <feather-icon
                  icon="SearchIcon"
                  class="text-muted"
                />
              </b-input-group-append>
            </b-input-group>
          </div> -->

          <portfolios-table
            :table-data="portfolios"
            :loading="portfoliosLoading"
            :actions="true"
            @row-clicked="portfolioClicked"
            @portfolioDeleted="portfolioDeleted"
            @create-portfolio="createPortfolioModal.show = true"
          />
        </b-col>
      </b-row>
    </div>
    <div v-else-if="portfolios.length < 1">
      <div class="d-flex flex-column justify-content-center align-items-center py-5">
        <div class="py-3 mx-5 text-center">
          <p>You do not have any portfolios yet.</p>
          <p>You can search for products to create a portfolio from your search results. You can also create an empty portfolio and add products to it later.</p>
        </div>

        <div class="mt-1">
          <b-button
            variant="primary"
            class="mr-1"
            @click.prevent="$router.push({ name: 'full-database' })"
          >
            Go To Products
          </b-button>
          <b-button
            variant="primary"
            @click.prevent="createPortfolioModal.show = true"
          >
            Create Portfolio
          </b-button>
        </div>
      </div>
    </div>
    <error-display
      v-else
      @action-clicked="getuser"
    />
    <b-modal
      v-model="createPortfolioModal.show"
      centered
      title="Create Empty Portfolio"
      hide-footer
    >
      <create-portfolio-modal-content
        :portfolios="portfolios"
        description="empty"
        @portfolio-created="portfolioCreated"
      />
    </b-modal>
  </div>
</template>

<script>
import {
  BRow, BCol, BButton,
} from 'bootstrap-vue'
import { $themeColors } from '@themeConfig'
import CreatePortfolioModalContent from '@/views/components/CreatePortfolioModalContent.vue'
import EcommerceStatistics from '@/views/ecommerce/EcommerceStatistics.vue'
import HintPopup from '@/views/components/HintPopup.vue'
import FutureMaturitiesChart from '@/views/products/portfolio/charts/FutureMaturitiesChart.vue'
import EcommercePieChart from '@/views/ecommerce/EcommercePieChart.vue'
import PortfoliosTable from '@/views/products/portfolio/PortfoliosTable.vue'
import ErrorDisplay from '@/views/components/ErrorDisplay.vue'
import { mapGetters } from 'vuex'
import UpgradeToPremium from '@/views/components/UpgradeToPremium.vue'

export default {
  name: 'Portfolios',
  components: {
    BRow,
    BCol,
    BButton,
    EcommerceStatistics,
    HintPopup,
    FutureMaturitiesChart,
    EcommercePieChart,
    PortfoliosTable,
    CreatePortfolioModalContent,
    ErrorDisplay,
    UpgradeToPremium,
  },
  data() {
    return {
      charts: {
        portfolioSummary: [],
        futureMaturities: {
          categories: [1, 2, 3, 4],
          series: [
            {
              name: 'Maturities',
              data: [],
            },
          ],
        },
        underlyings: {
          title: 'Underlyings',
          explanation: 'Underlying breakdowns of products in saved portfolios',
          labels: [],
          series: [],
          colors: [$themeColors.success, '#28c76f66', '#28c76f33'],
        },
        issuers: {
          title: 'Counterparties',
          explanation: 'Counterparty breakdowns of products in saved portfolios',
          labels: [],
          series: [],
          colors: [$themeColors.primary, '#1a73e866', '#1a73e833'],
        },
        productTypes: {
          title: 'Product Types',
          explanation: 'Products type breakdowns of products in saved portfolios',
          labels: [],
          series: [],
          colors: [$themeColors.warning, '#ff9f4366', '#ff9f4333'],
        },
      },
      clickedPortfolioId: '',
      items: [],
      loading: true,
      createPortfolioModal: {
        show: false,
      },
      portfolioError: null,
    }
  },
  computed: {
    ...mapGetters('auth', [
      'isPremium',
    ]),
    portfolios() {
      return this.$store.state.app.portfolios
    },
    portfoliosLoading() {
      return this.$store.state.app.portfolioLoading
    },
  },
  created() {
    if (this.isPremium) {
      this.$store.dispatch('app/updatePortfolios')
      this.getuser()
    }
  },
  activated() {
    if (this.isPremium) {
      this.$store.commit('app/SET_PORTFOLIO_UPDATED', false)
      this.$store.dispatch('app/updatePortfolios')
      this.getuser()
    }
  },
  methods: {

    getuser() {
      this.loading = true
      this.portfolioError = null

      this.$http
        .get('lifecycle/public/portfolio.cfc?method=getUserPortfolioInfo')
        .then(response => {
          this.charts.portfolioSummary = response.data.userproductuniverse.items
          this.charts.portfolioSummaryUpdated = response.data.userproductuniverse.updated
          this.charts.futureMaturities = response.data.portfoliosummary.futurematurities
          this.charts.underlyings = {
            ...this.charts.underlyings,
            series: response.data.portfoliosummary.underlyings.series,
            labels: response.data.portfoliosummary.underlyings.labels,
          }
          this.charts.issuers = {
            ...this.charts.issuers,
            series: response.data.portfoliosummary.issuers.series,
            labels: response.data.portfoliosummary.issuers.labels,
          }
          this.charts.productTypes = {
            ...this.charts.productTypes,
            series: response.data.portfoliosummary.producttypes.series,
            labels: response.data.portfoliosummary.producttypes.labels,
          }
          this.items = response.data.portfolioactivity
          this.loading = false
        })
        .catch(() => {
          this.portfolioError = true
          this.loading = false
        })
    },
    portfolioClicked(item, openInNewTab = false) {
      this.clickedPortfolioId = item.portfolioId
      if (openInNewTab) {
        const route = this.$router.resolve({ name: 'portfolio-report', params: { portfolioType: this.clickedPortfolioId } })
        window.open(route.href, '_blank')
      } else {
        this.$router.push({ name: 'portfolio-report', params: { portfolioType: this.clickedPortfolioId } })
      }
      return item
    },
    portfolioCreated() {
      this.createPortfolioModal.show = false
      this.$bvToast.toast('Portfolio Created', {
        title: 'Success',
        variant: 'success',
        solid: true,
      })

      this.$store.dispatch('app/updatePortfolios')
      this.getuser()
    },
    portfolioDeleted() {
      this.getuser()
      this.$store.dispatch('app/updatePortfolios')
    },
  },

}
</script>

<style scoped>
.saved-portfolios-card-with-hint {
  position: relative;
}

.saved-portfolios-card-with-hint ::v-deep .card-header {
  padding-right: 4rem;
}

.saved-portfolios-hint-popup {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
}
</style>

<style lang="scss">
@import '@core/scss/vue/pages/dashboard-ecommerce.scss';
@import '@core/scss/vue/libs/chart-apex.scss';
</style>
